import { useCapability } from '@vimeo/core/hooks/use-capability';
import { useOrionSettings } from '@vimeo/orion';

export interface B2BRepackagingContext {
  areBusinessPlansEnforced: boolean;
  isWhitelistedForIndPlans: boolean;
  showIndividualPlans: boolean;
  /**
   * True once the user has already submitted the `b2b_repack` survey
   * (regardless of qualifying outcome) or had a CS-issued grant written. The
   * survey is one-shot on the backend (`B2BRepackSurvey::applySurveyResponse`
   * throws `INVALID_INPUT` on resubmit), so the FE must hide every entry
   * point that would let the user re-open the modal.
   */
  hasSubmittedSurvey: boolean;
  /**
   * Derived gate for the "Request eligibility for dedicated plans" CTAs
   * (banner sub-line + upgrade-plan tertiary). True when the user is enforced
   * into the B2B set AND isn't whitelisted. The CTA remains visible after the
   * user has already submitted the survey and been rejected — callers handle
   * that case by passing `initialStep: 'not_qualified'` to the eligibility
   * modal so the click re-displays the final rejection screen instead of
   * re-opening the (one-shot) survey.
   */
  canRequestEligibility: boolean;
}

/**
 * Single accessor for the B2B repackaging "Segment A" gating model.
 *
 * - `areBusinessPlansEnforced` mirrors the `business_plans_enforced` Orion
 *   setting, which is overridden to `true` by the `business_plans_enforced`
 *   segment of the `b2b_repackaging_individual_plans` Janus experiment.
 * - `isWhitelistedForIndPlans` is true once the capability has resolved and the user has
 *   a `b2b_repack` survey row recorded (eligibility-modal submission or
 *   CS-issued grant). Until the capability fetch resolves we treat the user
 *   as not-yet-whitelisted so we don't briefly flash individual plans to a
 *   Segment A user.
 * - `showIndividualPlans` is the derived gate every UI consumer should branch
 *   on. Equivalent to "not enforced, or enforced but already whitelisted".
 *
 * Tier-based gating (e.g. only show the billing-page banner when on
 * Studio/Production) is the caller's responsibility — this hook is purely
 * segment + whitelist.
 *
 * **No cache invalidation primitive**: `useCapability` keeps a singleton
 * in-memory cache with no TTL and whose `useEffect` deps don't include any
 * external invalidation signal. Once the cap resolves, it's pinned for the
 * page lifetime. Flows that flip the capability (the eligibility modal,
 * CS-driven grants/revokes) should call `window.location.reload()` to pick
 * up the new value — see `IndividualEligibilityModal`. A soft re-fetch via
 * subscribe-and-rerender does NOT work here because the cap stays missing
 * from cache on re-render without triggering a new fetch.
 */
export function useB2BRepackagingContext(): B2BRepackagingContext {
  const { settings } = useOrionSettings();
  const areBusinessPlansEnforced = settings.business_plans_enforced;

  const { capabilities, ready } = useCapability([
    'individualPlansWhitelisted',
    'repackSurveyCompleted',
  ]);
  const isWhitelistedForIndPlans = ready && Boolean(capabilities.individualPlansWhitelisted);
  const hasSubmittedSurvey = ready && Boolean(capabilities.repackSurveyCompleted);

  return {
    areBusinessPlansEnforced,
    isWhitelistedForIndPlans,
    showIndividualPlans: !areBusinessPlansEnforced || isWhitelistedForIndPlans,
    hasSubmittedSurvey,
    canRequestEligibility: ready && areBusinessPlansEnforced && !isWhitelistedForIndPlans,
  };
}
